import axios from "axios";
import { isPublicRecordingUrl, parsePublicSesCdrId } from "@/utils/publicRecordingUrl";

function applyRecordingUrlToPayload(payload, { recordingUrl, cdrId }) {
  const cdrFromUrl = parsePublicSesCdrId(recordingUrl);
  if (cdrFromUrl && !cdrId) {
    payload.source = "cdr";
    payload.cdr_id = cdrFromUrl;
    return payload;
  }
  if (!recordingUrl) {
    throw new Error("Ses URL gerekli");
  }
  payload.source = "url";
  payload.recording_url = recordingUrl;
  return payload;
}

const defaultFilename = (cdrId) => (cdrId ? `kayit-${cdrId}.wav` : "recording.wav");

const NOT_FOUND_MESSAGE = "Ses kaydı bulunamadı. Dosya henüz sisteme aktarılmamış olabilir; lütfen birkaç dakika sonra tekrar deneyin.";

const isAwsErrorPayload = (text) => {
  if (!text) return false;
  return text.includes("<Error>") || text.includes("AccessDenied") || text.includes("Access Denied");
};

const parseErrorFromBlob = async (blob) => {
  const text = await blob.text();
  if (isAwsErrorPayload(text)) {
    return NOT_FOUND_MESSAGE;
  }
  try {
    const json = JSON.parse(text);
    return json.detail || NOT_FOUND_MESSAGE;
  } catch {
    return text || "Ses kaydı indirilemedi";
  }
};

const parseAxiosDownloadError = async (error) => {
  const data = error.response?.data;
  if (data instanceof Blob) {
    return parseErrorFromBlob(data);
  }
  if (data?.detail) {
    return data.detail;
  }
  if (error.response?.status === 404 || error.response?.status === 403) {
    return NOT_FOUND_MESSAGE;
  }
  return error.message || "Ses kaydı indirilemedi";
};

/**
 * Panel üzerinden yavaşlatmalı ses kaydı indirme.
 * CDN URL istemciye gönderilmez — yalnızca kayıt referansı (ID) POST edilir.
 */
export async function downloadRecordingViaPanel({
  apiBase,
  cdrId = null,
  source = "cdr",
  deviceId = null,
  recordingId = null,
  recordingUrl = null,
  filename = "",
}) {
  const payload = { source: source || "cdr" };

  if (payload.source === "url" || payload.source === "direct_url") {
    applyRecordingUrlToPayload(payload, { recordingUrl, cdrId });
  } else if (payload.source === "cdr") {
    if (!cdrId) {
      throw new Error("Kayıt ID bulunamadı");
    }
    payload.cdr_id = cdrId;
  } else {
    if (!deviceId || !recordingId) {
      throw new Error("Kayıt referansı eksik");
    }
    payload.device_id = deviceId;
    payload.recording_id = recordingId;
  }

  if (filename) {
    payload.filename = filename;
  }

  let response;
  try {
    response = await axios.post(`${apiBase}/recording-download`, payload, {
      responseType: "blob",
      timeout: 0,
      validateStatus: (status) => status < 500,
    });
  } catch (error) {
    throw new Error(await parseAxiosDownloadError(error));
  }

  const contentType = response.headers["content-type"] || "";
  const isJson = contentType.includes("application/json");
  const isXml = contentType.includes("xml");
  const blob = response.data;

  if (response.status >= 400 || isJson || isXml) {
    throw new Error(await parseErrorFromBlob(blob));
  }

  if (blob instanceof Blob && blob.size < 8192) {
    const peek = await blob.slice(0, Math.min(blob.size, 1024)).text();
    if (isAwsErrorPayload(peek)) {
      throw new Error(NOT_FOUND_MESSAGE);
    }
  }

  const disposition = response.headers["content-disposition"] || "";
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
  const suggestedName = decodeURIComponent(match?.[1] || match?.[2] || filename || defaultFilename(cdrId));

  const fileBlob = new Blob([blob], {
    type: response.headers["content-type"] || "audio/wav",
  });
  const objectUrl = window.URL.createObjectURL(fileBlob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.setAttribute("download", suggestedName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);

  return { filename: suggestedName, size: fileBlob.size };
}

async function fetchRecordingBlobFromPanel({
  apiBase,
  cdrId = null,
  source = "url",
  recordingUrl = null,
  deviceId = null,
  recordingId = null,
  onDownloadProgress = null,
}) {
  const payload = { source: source || "cdr" };

  if (payload.source === "url" || payload.source === "direct_url") {
    applyRecordingUrlToPayload(payload, { recordingUrl, cdrId });
  } else if (payload.source === "cdr") {
    if (!cdrId) {
      throw new Error("Kayıt ID bulunamadı");
    }
    payload.cdr_id = cdrId;
  } else {
    if (!deviceId || !recordingId) {
      throw new Error("Kayıt referansı eksik");
    }
    payload.device_id = deviceId;
    payload.recording_id = recordingId;
  }

  let response;
  try {
    response = await axios.post(`${apiBase}/recording-download`, payload, {
      responseType: "blob",
      timeout: 0,
      validateStatus: (status) => status < 500,
      onDownloadProgress: typeof onDownloadProgress === "function"
        ? (event) => {
            const loaded = event.loaded || 0;
            if (event.total) {
              onDownloadProgress(Math.min(1, loaded / event.total), loaded, event.total);
            } else if (loaded > 0) {
              onDownloadProgress(undefined, loaded, null);
            }
          }
        : undefined,
    });
  } catch (error) {
    throw new Error(await parseAxiosDownloadError(error));
  }

  const contentType = response.headers["content-type"] || "";
  const isJson = contentType.includes("application/json");
  const isXml = contentType.includes("xml");
  const blob = response.data;

  if (response.status >= 400 || isJson || isXml) {
    throw new Error(await parseErrorFromBlob(blob));
  }

  if (blob instanceof Blob && blob.size < 8192) {
    const peek = await blob.slice(0, Math.min(blob.size, 1024)).text();
    if (isAwsErrorPayload(peek)) {
      throw new Error(NOT_FOUND_MESSAGE);
    }
  }

  return new Blob([blob], {
    type: response.headers["content-type"] || "audio/wav",
  });
}

const toAbsoluteRecordingUrl = (recordingUrl) => {
  const u = String(recordingUrl || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (u.startsWith("/")) return `${origin}${u}`;
  return `${origin}/api/${u.replace(/^\/+/, "")}`;
};

export function buildRecordingPlayStreamUrl({ apiBase, recordingUrl, cdrId = null, deviceId = null, recordingId = null }) {
  const base = String(apiBase || "/api").replace(/\/+$/, "");
  const params = new URLSearchParams();
  if (recordingUrl) {
    params.set("source", "url");
    params.set("recording_url", String(recordingUrl).trim());
  } else if (cdrId) {
    params.set("source", "cdr");
    params.set("cdr_id", String(cdrId));
  } else if (deviceId && recordingId) {
    params.set("device_id", String(deviceId));
    params.set("recording_id", String(recordingId));
  }
  return `${base}/recording-play?${params.toString()}`;
}

/**
 * @returns {Promise<{ kind: 'stream'|'direct'|'blob', src: string }>}
 */
export async function resolveRecordingAudioSpec({ apiBase, recordingUrl }) {
  const raw = String(recordingUrl || "").trim();
  if (!raw) {
    throw new Error("Ses URL gerekli");
  }
  const absolute = toAbsoluteRecordingUrl(raw);
  if (isPublicRecordingUrl(absolute) || isPublicRecordingUrl(raw)) {
    return {
      kind: "direct",
      src: isPublicRecordingUrl(absolute) ? absolute : toAbsoluteRecordingUrl(raw),
    };
  }
  if (!apiBase) {
    throw new Error("API yapılandırması eksik");
  }
  /** Panel üzerinden WAV: tarayıcı akışı Content-Length olmadan takılıyor; dinleme = indir + oynat */
  return {
    kind: "blob",
    src: "",
    pendingDownload: true,
  };
}

/** Yedek: tam dosyayı indirip blob URL (ilerleme callback isteğe bağlı) */
export async function fetchRecordingBlobObjectUrl({ apiBase, recordingUrl, cdrId = null, onDownloadProgress }) {
  const cdrFromUrl = parsePublicSesCdrId(recordingUrl);
  const fileBlob = await fetchRecordingBlobFromPanel({
    apiBase,
    source: cdrFromUrl || cdrId ? "cdr" : "url",
    cdrId: cdrId || cdrFromUrl,
    recordingUrl: cdrFromUrl || cdrId ? null : recordingUrl,
    onDownloadProgress,
  });
  return URL.createObjectURL(fileBlob);
}

/** @deprecated resolveRecordingAudioSpec kullanın */
export async function resolveRecordingAudioSrc({ apiBase, recordingUrl }) {
  const spec = await resolveRecordingAudioSpec({ apiBase, recordingUrl });
  if (spec.kind === "blob") return spec.src;
  if (spec.kind === "direct" || spec.kind === "stream") return spec.src;
  return spec.src;
}

export async function downloadRecordingFromUrl({ apiBase, recordingUrl, filename = "" }) {
  if (!recordingUrl) {
    throw new Error("Ses URL gerekli");
  }
  return downloadRecordingViaPanel({
    apiBase,
    source: "url",
    recordingUrl,
    filename,
  });
}

export default downloadRecordingViaPanel;

export const isRecordingNotFoundMessage = (message) => /bulunamad/i.test(String(message || ""));
